import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Home.css";

// normalize API URL and ensure no trailing slash (helps when concatenating paths)
// fall back gracefully if the env var accidentally points at the frontend itself

// ✅ Best approach for same-domain deployment
const API_URL = process.env.NODE_ENV === "production" 
  ? ""   // empty string = same origin, so /api/auth/login works perfectly
  : (process.env.REACT_APP_API_URL || "http://localhost:5000");

console.log("Current API URL:", API_URL || "same origin (production)");


export default function HomeScreen() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: "", password: "" });
  const [isLogin, setIsLogin] = useState(true);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async () => {
    setError("");
    setLoading(true);
    const endpoint = isLogin ? "login" : "register";
    const url = `${API_URL}/api/auth/${endpoint}`;
    console.log('Auth request URL:', url);
    try {
      const res = await fetch(url, {
        method: "POST",
        mode: "cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      // read as text first so we can report non-JSON payloads
      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (parseErr) {
        // give the developer as much context as possible when an HTML page is
        // returned (usually because the request went to the wrong server/origin)
        console.error('Expected JSON but got:', text);
        throw new Error(
          `Unexpected response from server (status ${res.status}). ` +
            `URL used was ${url}`
        );
      }

      if (data.token) {
        // If Remember Me checked → persist token, else store temporarily
        if (rememberMe) {
          localStorage.setItem("token", data.token);
          localStorage.setItem("username", form.username);
        } else {
          sessionStorage.setItem("token", data.token);
          sessionStorage.setItem("username", form.username);
        }

        navigate("/dashboard");
      } else {
        setError(data.error || data.message || "Something went wrong");
      }
    } catch (err) {
      // Log full error for debugging in dev console
      console.error('Fetch error during auth request:', err);
      setError(`Connection error: ${err.message} (${err.name})`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Show which API URL the running frontend is using
    console.log('Frontend API_URL at runtime:', API_URL);
    // warn early if the URL looks like the client itself (common mis‑config)
    try {
      const u = new URL(API_URL);
      if (u.port === window.location.port) {
        console.warn('API_URL appears to be the same origin.  Ensure the backend is running on a different port.');
      }
    } catch {}
  }, []);

  const handleKeyPress = (e) => {
    if (e.key === "Enter") handleSubmit();
  };

  return (
    <div className="home-container">
      <div className="home-background">
        <div className="blob blob1"></div>
        <div className="blob blob2"></div>
        <div className="blob blob3"></div>
      </div>
      
      <div className="home-content">
        <div className="home-left">
          <div className="welcome-section">
            <h1>SelfPivot</h1>
            <p className="tagline">Track Your Progress, Master Your Goals</p>
            <p className="description">
              Transform your daily routines into measurable progress. Track hours, 
              manage goals, and visualize your growth across daily, weekly, monthly, 
              and quarterly reviews.
            </p>
            <ul className="features-list">
              <li>📊 Real-time analytics dashboard</li>
              <li>🎯 Goal tracking and management</li>
              <li>📈 Time series visualizations</li>
              <li>✅ Task and achievement logging</li>
              <li>🔒 Secure authentication</li>
            </ul>
          </div>
        </div>

        <div className="home-right">
          <div className="auth-card">
            <div className="auth-header">
              <h2>{isLogin ? "Welcome Back" : "Create Account"}</h2>
              <p>{isLogin ? "Sign in to continue" : "Join our community"}</p>
            </div>

            {error && (
              <div className="error-message">
                <span>⚠️ {error}</span>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="username">Username</label>
              <input
                id="username"
                type="text"
                name="username"
                placeholder="Enter your username"
                value={form.username}
                onChange={handleChange}
                onKeyPress={handleKeyPress}
                disabled={loading}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                name="password"
                placeholder="Enter your password"
                value={form.password}
                onChange={handleChange}
                onKeyPress={handleKeyPress}
                disabled={loading}
                className="form-input"
              />
            </div>

            {isLogin && (
              <div className="form-checkbox">
                <input
                  type="checkbox"
                  id="rememberMe"
                  checked={rememberMe}
                  onChange={() => setRememberMe(!rememberMe)}
                  disabled={loading}
                />
                <label htmlFor="rememberMe">Remember me</label>
              </div>
            )}

            <button
              onClick={handleSubmit}
              disabled={loading || !form.username || !form.password}
              className="submit-button"
            >
              {loading ? (
                <>
                  <span className="spinner"></span> Processing...
                </>
              ) : (
                isLogin ? "Sign In" : "Create Account"
              )}
            </button>

            <div className="form-divider">
              <span>or</span>
            </div>

            <button
              type="button"
              onClick={() => {
                setIsLogin(!isLogin);
                setError("");
              }}
              className="toggle-button"
            >
              {isLogin ? (
                <>New here? <strong>Create Account</strong></>
              ) : (
                <>Already have an account? <strong>Sign In</strong></>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
 